import React, { useEffect, useState, useRef } from "react";
import ClutchPickView from "./ClutchPickView";
import CircleProgress from "./CircleProgress";
import Card from "./Card";
import TokenFastForwardMenu from "./TokenFastForwardMenu";
import SteamLoginButton from "./SteamLoginButton";
import { useClutchPicks } from "../hooks/useClutchPicks";
import { useUser } from "../hooks/useUser";

const ClutchPicks = () => {
  const {
    clutchPicks,
    tokenLastUsed,
    fetchClutchPicks,
    openClutchPick,
    getFastForwardPrice,
    fastForwardClutchPickToken,
    fastForwardLoading,
    fastForwardError,
    loading,
    error,
  } = useClutchPicks();
  const { isGuest } = useUser();
  const [selectedPick, setSelectedPick] = useState(null);
  const [remainingCards, setRemainingCards] = useState([]);
  const [openedCard, setOpenedCard] = useState(null);
  // shufflePhase: 'none' | 'center' | 'return'
  const [shufflePhase, setShufflePhase] = useState("none");
  const shuffleTimeoutRef = useRef();
  // For card flip animation
  const cardRefs = useRef([]);
  const timers = useRef([]);
  const [isOpeningStates, setIsOpeningStates] = useState([]); // Track which cards are opening (animation)
  const [showBackStates, setShowBackStates] = useState([]); // Track which cards show back
  const [lastClickedIndex, setLastClickedIndex] = useState(null); // Track which card was clicked
  const [cardClickLoading, setCardClickLoading] = useState(false); // Prevent multiple clicks

  const { user } = useUser();

  const closePopUp = () => {
    setSelectedPick(null);
    setShufflePhase("none");
    setRemainingCards([]);
    setOpenedCard(null);
    setIsOpeningStates([]);
    setShowBackStates([]);
    setLastClickedIndex(null);
    setCardClickLoading(false);
    cardRefs.current = [];
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current = [];
  };

  // Handle card click in popup
  const handleCardClick = async (clickedIndex) => {
    if (
      !selectedPick ||
      !selectedPick._id ||
      shufflePhase !== "none" ||
      cardClickLoading ||
      lastClickedIndex
    )
      return;

    setCardClickLoading(true);
    setLastClickedIndex(clickedIndex);

    try {
      const result = await openClutchPick(selectedPick._id);

      // Enhanced validation for API response
      if (!result || !result.card || !Array.isArray(result.players)) {
        console.error("Invalid open clutch pick result:", result);
        return;
      }

      // Filter out any null/undefined players that might have come from backend
      const validRemainingPlayers = result.players.filter(
        (player) => player && player.player && player.player._id
      );

      // Validate that we have the expected number of remaining cards
      const expectedRemainingCount = selectedPick.players.length - 1;
      if (validRemainingPlayers.length !== expectedRemainingCount) {
        console.warn(
          `Expected ${expectedRemainingCount} remaining cards, got ${validRemainingPlayers.length} valid cards (${result.players.length} total from API)`,
          {
            validPlayers: validRemainingPlayers,
            allPlayers: result.players,
            selectedPickPlayers: selectedPick.players,
          }
        );

        // If we have some valid players but not the expected count,
        // we can still proceed but log the issue
        if (validRemainingPlayers.length === 0) {
          console.error("No valid remaining players received from API");
          return;
        }
      }

      console.log("Opened card:", result.card);
      console.log("Valid remaining cards:", validRemainingPlayers);
      setOpenedCard(result.card);
      setRemainingCards(validRemainingPlayers);
    } catch (error) {
      console.error("Error opening clutch pick:", error);
    } finally {
      setCardClickLoading(false);
    }
  };

  // Fast forward popup state
  const [showFastForwardMenu, setShowFastForwardMenu] = useState(false);
  const [fastForwardPrice, setFastForwardPrice] = useState(0);
  const [fastForwardTimeLeft, setFastForwardTimeLeft] = useState("");

  const handleFastForward = async () => {
    const fastForwardResult = await fastForwardClutchPickToken();
    // close the fast forward menu after successful fast forward
    if (fastForwardResult) {
      setShowFastForwardMenu(false);
    }
  };

  const closeFastForwardMenu = () => {
    setShowFastForwardMenu(false);
  };

  // Handle clutch pick selection
  const onSelectClutchPick = async (pick) => {
    if (!pick || !pick._id) return;
    // Check if token is available
    if (!tokenLastUsed) {
      return;
    }

    if (pick.isCompleted) {
      return;
    }

    const now = new Date();
    const expire = new Date(pick.expireDate);
    const fiveHoursBefore = new Date(expire.getTime() - 5 * 60 * 60 * 1000);
    const diff = (user?.isPremium ? expire : fiveHoursBefore) - now;
    if (diff <= 0) {
      return;
    }

    const lastUsed = new Date(tokenLastUsed);
    const nextAvailable = new Date(lastUsed.getTime() + 24 * 60 * 60 * 1000);
    if (now < nextAvailable) {
      // Token is not available yet - get price from backend
      try {
        const priceData = await getFastForwardPrice();
        if (priceData) {
          setFastForwardPrice(priceData.price);
          setFastForwardTimeLeft(priceData.timeLeft.formatted);
          setShowFastForwardMenu(true);
        } else {
          console.error("Failed to get fast forward price");
        }
      } catch (error) {
        console.error("Error getting fast forward price:", error);
      }
      return;
    }

    // Reset states and set selected pick
    setShufflePhase("none");
    setSelectedPick(pick);
    setRemainingCards([]);
    setOpenedCard(null);
    setIsOpeningStates([]);
    setShowBackStates([]);
    setLastClickedIndex(null);
    setCardClickLoading(false);
    cardRefs.current = [];
  };

  // Flip cards one by one: set isOpening true, after 1s set showBack false and call openCard
  const startFlipAnimation = (clickedIndex, players, card) => {
    let flipOrder = [];
    if (selectedPick && selectedPick.players) {
      for (let i = 0; i < selectedPick.players.length; i++) {
        if (i !== clickedIndex) flipOrder.push(i);
      }
      if (!flipOrder.includes(clickedIndex)) {
        flipOrder.push(clickedIndex);
      }
    }
    // Reset all states, showBack true for all cards
    setIsOpeningStates(Array(selectedPick.players.length).fill(false));
    setShowBackStates(Array(selectedPick.players.length).fill(true));
    // Flip one by one
    flipOrder.forEach((cardIdx, i) => {
      // Step 1: set isOpening true
      const timer1 = setTimeout(() => {
        setIsOpeningStates((prev) => {
          const updated = [...prev];
          updated[cardIdx] = true;
          return updated;
        });
        // Step 2: after 1s, set showBack false and call openCard
        const timer2 = setTimeout(() => {
          setShowBackStates((prev) => {
            const updated = [...prev];
            updated[cardIdx] = false;
            return updated;
          });
          if (
            cardRefs.current[cardIdx] &&
            typeof cardRefs.current[cardIdx].openCard === "function"
          ) {
            cardRefs.current[cardIdx].openCard();
            console.log(`Card ${cardIdx} opened`);
          }
        }, 300);
        timers.current.push(timer2);
      }, i * 1000); // 1200ms between cards
      timers.current.push(timer1);
    });
  };

  // Ensure showBackStates is true for all cards when popup opens
  useEffect(() => {
    if (selectedPick && selectedPick.players) {
      setShowBackStates(Array(selectedPick.players.length).fill(true));
    }
  }, [selectedPick]);

  // Start flip animation after openedCard and remainingCards are set
  useEffect(() => {
    if (
      openedCard &&
      remainingCards.length > 0 &&
      lastClickedIndex !== null &&
      selectedPick
    ) {
      // Validate that we have the expected number of remaining cards before starting animation
      const expectedRemainingCount = selectedPick.players.length - 1;
      console.log(
        `Animation validation: Expected ${expectedRemainingCount}, got ${remainingCards.length}, totalPlayers: ${selectedPick.players.length}, lastClickedIndex: ${lastClickedIndex}`
      );

      // More flexible validation - allow slightly less data if close to expected
      const minAcceptableCount = Math.max(1, expectedRemainingCount - 1); // Allow 1 less than expected
      if (remainingCards.length < minAcceptableCount) {
        console.warn(
          `Animation blocked: Expected at least ${minAcceptableCount} remaining cards, got ${remainingCards.length}. Waiting for more complete data...`
        );
        return; // Don't start animation if data is too incomplete
      }

      if (remainingCards.length !== expectedRemainingCount) {
        console.warn(
          `Animation proceeding with incomplete data: Expected ${expectedRemainingCount}, got ${remainingCards.length}`
        );
      }

      // Additional validation: ensure we can map all card indices properly with available data
      let mappingValid = true;
      let expectedRemainingIndex = 0;
      for (let i = 0; i < selectedPick.players.length; i++) {
        if (i !== lastClickedIndex) {
          if (expectedRemainingIndex >= remainingCards.length) {
            console.error(
              `Invalid mapping: card index ${i} -> remainingIdx ${expectedRemainingIndex}, but remainingCards.length is ${remainingCards.length}`
            );
            mappingValid = false;
          }
          expectedRemainingIndex++;
        }
      }

      if (!mappingValid) {
        console.error("Animation cancelled due to invalid index mapping");
        return;
      }

      // Longer delay to ensure API response is fully processed and state is stable
      const timer = setTimeout(() => {
        console.log("Starting flip animation with available data:", {
          openedCard: openedCard.name || openedCard.id,
          remainingCardsCount: remainingCards.length,
          expectedCount: expectedRemainingCount,
        });
        startFlipAnimation(lastClickedIndex, remainingCards, openedCard);
      }, 300); // Increased to 300ms for more stability
      return () => clearTimeout(timer);
    }
  }, [openedCard, remainingCards, lastClickedIndex, selectedPick]);

  // No need for useEffect to call openCard, handled in startFlipAnimation

  useEffect(() => {
    fetchClutchPicks();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (clutchPicks && clutchPicks.clutchPick) {
      console.log("Fetched clutch picks:", clutchPicks);
    }
    // eslint-disable-next-line
  }, [clutchPicks]);

  // Timer state for live countdown and progress
  const [timer, setTimer] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let interval;
    if (tokenLastUsed) {
      const updateTimer = () => {
        const lastUsed = new Date(tokenLastUsed);
        const now = new Date();
        const nextAvailable = new Date(
          lastUsed.getTime() + 24 * 60 * 60 * 1000
        );
        const diff = nextAvailable - now;
        if (diff <= 0) {
          setTimer("Available");
          setProgress(100);
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setTimer(`${hours}h ${minutes}m ${seconds}s`);
          // Progress: percent of 24h elapsed
          const total = 24 * 60 * 60 * 1000;
          const elapsed = now - lastUsed;
          setProgress(Math.max(0, Math.min(100, (elapsed / total) * 100)));
        }
      };
      updateTimer();
      interval = setInterval(updateTimer, 1000);
    } else {
      setTimer("Not available");
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [tokenLastUsed]);

  // Shuffle animation effect
  useEffect(() => {
    if (selectedPick) {
      setShufflePhase("center");
      shuffleTimeoutRef.current && clearTimeout(shuffleTimeoutRef.current);
      shuffleTimeoutRef.current = setTimeout(() => {
        setShufflePhase("return");
        shuffleTimeoutRef.current = setTimeout(() => {
          setShufflePhase("none");
        }, 500); // 500ms for return animation
      }, 500); // 500ms for center animation
    } else {
      setShufflePhase("none");
    }
    return () => {
      shuffleTimeoutRef.current && clearTimeout(shuffleTimeoutRef.current);
    };
  }, [selectedPick]);

  return (
    <div className={`main-container col-10 offset-1`}>
      <div
        className="title-container"
        style={{ justifyContent: "space-between" }}
      >
        <h1 className="title">Clutch Picks</h1>
        <div className="teamWinner-info">
          <div className="teamWinner-box">{timer}</div>
          <CircleProgress percent={progress} />
        </div>
      </div>
      <div className="scroll-container">
        {Array.isArray(clutchPicks) && clutchPicks.length > 0 && user ? (
          clutchPicks.map((pick, idx) => (
            <ClutchPickView
              key={idx}
              parsedClutchPick={pick}
              isPremium={user?.isPremium}
              onSelect={() => onSelectClutchPick(pick)}
            />
          ))
        ) : !loading && !error && user ? (
          <p className="no-cards">No clutch picks found</p>
        ) : null}

        {isGuest && (
          <div className="clutch-pick-menu-container">
            <div className="fantasyTeamView-content">
              <div
                className="event-fantasy-team-container"
                style={{ justifyContent: "center", alignItems: "center" }}
              >
                <SteamLoginButton />
              </div>
            </div>
          </div>
        )}

        {(loading || (!user && !isGuest)) && <div className="loader"></div>}
      </div>

      {showFastForwardMenu && (
        <div className="overlay" onClick={closeFastForwardMenu}>
          <TokenFastForwardMenu
            price={fastForwardPrice}
            onFastForward={handleFastForward}
            onCancel={closeFastForwardMenu}
            timeLeft={fastForwardTimeLeft}
            loading={fastForwardLoading}
            error={fastForwardError}
          />
        </div>
      )}

      {selectedPick && (
        <div className="popup-overlay">
          <div className="OpenPack-container">
            <div
              className="OpenPack-content col-10"
              style={{
                position: "relative",
                height: "25vw",
                minHeight: "10vw",
              }}
            >
              {selectedPick.players.map((player, index) => {
                // ...existing code...
                const total = selectedPick.players.length;
                const cardWidthVW = 15; // 15vw per card
                const gapVW = 0; // 0vw gap
                const containerWidthVW =
                  total * cardWidthVW + (total - 1) * gapVW;
                // Calculate original X position in vw
                const startXvw =
                  containerWidthVW / -2 +
                  index * (cardWidthVW + gapVW) +
                  cardWidthVW / 2;
                let style = {
                  position: "absolute",
                  width: `${cardWidthVW}vw`,
                  paddingBottom: 0,
                  marginBottom: 0,
                  cursor: "default",
                  transition:
                    "transform 0.25s cubic-bezier(.68,-0.55,.27,1.55)",
                  zIndex: shufflePhase === "center" ? 10 : 1,
                };
                if (shufflePhase === "center") {
                  style.left = "calc(50% - 9%)"; // 9% is half of 18% card width
                  style.transform = "scale(1.15)";
                } else {
                  style.left = "41%";
                  style.transform = `translateX(${startXvw}vw)`;
                }
                // If openedCard and remainingCards are set, show the openedCard at clicked index, others as remainingCards
                const cardKey = index;
                let cardData = null;
                let parsedPlayerData = player;
                if (
                  openedCard &&
                  remainingCards.length > 0 &&
                  lastClickedIndex !== null
                ) {
                  if (index === lastClickedIndex) {
                    cardData = openedCard;
                  } else {
                    // First check if we have the expected number of remaining cards
                    const expectedRemainingCount =
                      selectedPick.players.length - 1;
                    if (remainingCards.length !== expectedRemainingCount) {
                      // Data is incomplete, don't try to map - keep original player data
                      console.warn(
                        `Render skipped mapping due to incomplete data: expected ${expectedRemainingCount}, got ${remainingCards.length}`
                      );
                    } else {
                      // More robust mapping: calculate how many cards come before this index (excluding clicked card)
                      let remainingIdx = 0;
                      for (let i = 0; i < index; i++) {
                        if (i !== lastClickedIndex) {
                          remainingIdx++;
                        }
                      }

                      // Add bounds checking to prevent accessing undefined array elements
                      if (
                        remainingIdx >= 0 &&
                        remainingIdx < remainingCards.length
                      ) {
                        parsedPlayerData = remainingCards[remainingIdx];
                      } else {
                        console.warn(
                          `Invalid remainingIdx ${remainingIdx} for array length ${remainingCards.length}. Index: ${index}, lastClickedIndex: ${lastClickedIndex}, totalCards: ${selectedPick.players.length}`
                        );
                        // Keep original player data as fallback
                      }
                    }
                  }
                }
                return (
                  <div
                    key={cardKey}
                    className="cardDisplay-container shuffle-abs"
                    style={style}
                    onClick={() => handleCardClick(index)}
                  >
                    <div
                      className={`card-hoverable ${
                        index === lastClickedIndex && "highlighted-card"
                      }`}
                    >
                      <Card
                        card={cardData}
                        parsedPlayer={parsedPlayerData}
                        showBack={showBackStates[index]}
                        isOpening={isOpeningStates[index]}
                        ref={(el) => (cardRefs.current[index] = el)}
                        key={cardKey}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="OpenPack-footer">
              <button
                className="default-button close-button"
                onClick={closePopUp}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClutchPicks;
